import { useUserSession } from '@solinkcorp/authentication';
import { cn } from '@solinkcorp/solink-ui';
import { castDraft } from 'immer';
import React, { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useControllerState } from 'solox';
import { getLayoutAdoptionMetrics } from '../../../analytics/sdmAdoptionMetrics';
import { MAX_WIDGETS_PER_LAYOUT, SDM_EVENTS, SDM_ROUTE } from '../../../constants';
import { useSDMAnalytics } from '../../../contexts/AnalyticsContext';
import { type Controller } from '../../../Controller';
import {
  useDeleteServiceDisplayWidgets,
  useServiceDisplayLayouts,
  useUpdateServiceDisplayWidgets,
} from '../../../hooks/useServiceDisplayLayouts';
import { type ServiceDisplayLayout } from '../../../layout/types';
import styles from '../../../sdm.module.css';
import LayoutSelectorDialog from '../LayoutSelector/LayoutSelectorDialog';
import { SiteSelector } from '../SiteSelector';
import HeaderLeft from './HeaderLeft';
import HeaderRight from './HeaderRight';

interface HeaderProps {
  controller: Controller;
  canEdit: boolean;
}

export const VerticalDivider = () => {
  return <div className="h-6 w-px border-l border-border py-1" />;
};

/** Header for the  */
const Header: React.FC<HeaderProps> = (props) => {
  const user = useUserSession();
  const navigate = useNavigate();
  const analytics = useSDMAnalytics();
  const location = useLocation();
  const updateServiceDisplayWidgets = useUpdateServiceDisplayWidgets();
  const deleteServiceDisplayWidgets = useDeleteServiceDisplayWidgets();
  const { data: serviceDisplayLayouts } = useServiceDisplayLayouts();
  const { controller, canEdit } = props;
  const editingLayout = useControllerState(controller.state, (state) => state.editingLayout);
  const pendingWidgetDeletes = useControllerState(
    controller.state,
    (state) => state.pendingWidgetDeletes,
  );
  const chServiceDisplayLayout = serviceDisplayLayouts?.find(
    (layout: ServiceDisplayLayout) => layout.id === editingLayout?.id,
  );

  const sdmViewPath = `${SDM_ROUTE}${location.search}`;

  const addAction = useCallback(() => {
    analytics.trackSDMEvent(SDM_EVENTS.CREATE_WIDGET, {
      description: 'Added a widget to the service display monitor layout',
    });
    controller.updateLayoutWithNewConfig();
  }, [controller, analytics]);

  // Called to stop editing without saving.
  const cancelAction = useCallback(() => {
    analytics.trackSDMEvent(SDM_EVENTS.CANCEL_EDIT, {
      description: 'Canceled editing service display monitor layout',
    });
    navigate(sdmViewPath);
  }, [navigate, analytics, sdmViewPath]);

  // Called to save the layout and stop editing.
  const saveAction = useCallback(async () => {
    if (!editingLayout) {
      throw new Error('No layout to save');
    }
    const isNewLayout = !chServiceDisplayLayout;
    analytics.trackSDMEvent(SDM_EVENTS.SAVE_LAYOUT, {
      description: 'Saved the service display monitor layout',
      totalWidgets: editingLayout.widgets.length,
      layoutId: editingLayout.id,
      layoutName: editingLayout.name,
      layoutCustomerId: editingLayout.customerId,
      layoutCreatedAt: editingLayout.createdAt,
      layoutUpdatedAt: editingLayout.updatedAt,
      ...getLayoutAdoptionMetrics(editingLayout),
      isNewLayout,
      totalLayoutsForCustomer: (serviceDisplayLayouts?.length ?? 0) + (isNewLayout ? 1 : 0),
    });
    if (pendingWidgetDeletes.length > 0 && editingLayout.id) {
      await deleteServiceDisplayWidgets(editingLayout.id, Array.from(pendingWidgetDeletes));
    }
    await updateServiceDisplayWidgets(
      editingLayout.widgets ? castDraft(editingLayout.widgets) : [],
      chServiceDisplayLayout ? editingLayout?.id : undefined,
      editingLayout.name,
    );
    controller.clearPendingWidgetDeletes();
    navigate(sdmViewPath);
  }, [
    navigate,
    sdmViewPath,
    updateServiceDisplayWidgets,
    deleteServiceDisplayWidgets,
    editingLayout,
    chServiceDisplayLayout,
    pendingWidgetDeletes,
    controller,
    analytics,
    serviceDisplayLayouts?.length,
  ]);

  return (
    <div className={cn(styles.header, 'border-b border-border-muted')}>
      <HeaderLeft controller={controller} canEdit={canEdit} />
      <span className="text-center">{!editingLayout && <SiteSelector />}</span>
      <HeaderRight
        isEditing={!!editingLayout}
        hasMaxWidgets={editingLayout?.widgets.length === MAX_WIDGETS_PER_LAYOUT}
        addAction={addAction}
        cancelAction={cancelAction}
        saveAction={saveAction}
        canEdit={canEdit}
        user={user.profile?.name || user.jwtUser?.email || 'User'}
      />
      <LayoutSelectorDialog controller={controller} />
    </div>
  );
};

export default React.memo(Header);
